import { CheckIcon } from '@heroicons/react/20/solid';
import { useMutationLogConsent, useMutationSaveDevis } from '@workspace/core-ts/api';
import {
    BulleAide,
    ConsentRecontactLegal,
    CourtierEmailWarning,
    useCourtierEmailBlock,
    FieldAdresses,
    FieldCheckbox,
    FieldInput,
    FieldPatternInput,
    FieldRadioBlock,
    HorairesRecontact,
    ModalOptionRecontact,
    Question,
    SmartButton,
} from '@workspace/core-ts/components';
import { SmartFormGlobal, useGlobalForm, useSmartFormGlobal } from '@workspace/core-ts/contexts';
import { useAvenant, useSource } from '@workspace/core-ts/hooks';
import { OnlineActivation, Pin } from '@workspace/core-ts/icons-amo';
import { trackEmailQuote } from '@workspace/utils';
import { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { coordonneesContent, useContent } from '../../content';
import { isConsentementEmailRecueilli } from './consentement-email-step';
import type { StepContentConfig } from '../../utils';
import { MOTIF_AVENANT } from '../../utils/avenant';

const MOTIF_CHANGEMENT_ADRESSE = MOTIF_AVENANT.CHANGEMENT_ADRESSE;

interface CoordonneesStepProps {
    content?: StepContentConfig;
    showHeaderIcon?: boolean;
    headerAddressIcon?: React.ComponentType<any>;
    headerCoordonnesIcon?: React.ComponentType<any>;
    children?: React.ReactNode;
    validationCP?: Record<string, any>;
    /** Type de consentement: 'checkbox' (simple) ou 'radio' (J'accepte/Je refuse avec détails) */
    consentType?: 'checkbox' | 'radio';
    /** Afficher la notice de dématérialisation sous le champ email (source site uniquement) */
    showEmailDematerialisationNotice?: boolean;
    /** Proposer le lien "Je ne trouve pas mon adresse" pour saisir l'adresse manuellement */
    allowManualAddress?: boolean;
}

export function CoordonneesStep({
    content = coordonneesContent,
    showHeaderIcon = true,
    headerAddressIcon: HeaderAddressIcon = Pin,
    headerCoordonnesIcon: HeaderCoordonnesIcon = OnlineActivation,
    children,
    validationCP,
    consentType = 'checkbox',
    showEmailDematerialisationNotice = true,
    allowManualAddress = false,
}: CoordonneesStepProps) {
    const methods = useSmartFormGlobal();
    const r = useContent();
    const { meta, actions, others } = useGlobalForm();
    const mutationSaveDevis = useMutationSaveDevis(meta.token);
    const mutationLogConsent = useMutationLogConsent();
    const apporteur = others?.apporteur?.codeBelair;
    const setSearchParams = useSearchParams()[1];
    const { isDirect, isAprilMoto, canAskContactConsent } = useSource();
    const { validateNotCourtierEmail } = useCourtierEmailBlock();
    const isAvenant = useAvenant();
    const hasClearedRef = useRef(false);

    const recevoirEmail = methods.watch('RecevoirEmail');
    const motifAvenant = methods.watch('DemandeTarif.Police.MotifAvenant');

    // Vider les champs d'adresse pour le changement d'adresse
    useEffect(() => {
        if (isAvenant && motifAvenant === MOTIF_CHANGEMENT_ADRESSE && !hasClearedRef.current) {
            hasClearedRef.current = true;
            methods.setValue('DemandeTarif.ListePersonnes.0.Adresse1', '');
            methods.setValue('DemandeTarif.ListePersonnes.0.Adresse2', '');
            methods.setValue('DemandeTarif.ListePersonnes.0.CP', '');
            methods.setValue('DemandeTarif.ListePersonnes.0.Ville', '');
        }
    }, [isAvenant, motifAvenant, methods]);

    const showConsentCheckbox = isDirect && consentType === 'checkbox';
    // Consentements email et téléphone (radio vélo/trottinette) : uniquement pour une liste d'apporteurs autorisés.
    const showConsentRadio = canAskContactConsent && consentType === 'radio';
    // Pas de demande de consentement email sur un avenant : le client est déjà assuré.
    const showEmailConsent = canAskContactConsent && !isAvenant;
    // Déjà recueilli par l'étape « Votre devis est presque prêt » (après le tarif) : on ne le redemande pas
    const emailConsentDejaRecueilli = isConsentementEmailRecueilli(methods.watch('DemandeDevis.PixelConsent'));

    // Question non affichée (courtier, etc.) : consentement email à false par défaut
    useEffect(() => {
        if (!showEmailConsent) {
            methods.setValue('DemandeDevis.EmailContactConsent', false);
        }
    }, [showEmailConsent, methods]);

    const handleSubmit = async (data: any) => {
        const response = await mutationSaveDevis.mutateAsync(data);
        if (isDirect || canAskContactConsent) {
            const personne = data?.DemandeTarif?.ListePersonnes?.[0];
            mutationLogConsent.mutate({
                token: meta.token,
                canal: data?.DemandeTarif?.Police?.Canal ?? '',
                phone: personne?.TelPortable ?? '',
                email: personne?.Email ?? '',
                phone_contact_consent: data?.DemandeDevis?.PhoneContactConsent ?? false,
                email_contact_consent: data?.DemandeDevis?.EmailContactConsent ?? false,
                type: 'souscription',
            });
        }
        if (recevoirEmail) {
            trackEmailQuote(apporteur);
            setSearchParams({ modal: 'RecevoirEmail' });
            actions.saveData(response);
        } else {
            actions.saveAndGoNextStep(response.data);
        }
        return response.data;
    };

    const buttonText = recevoirEmail ? 'Recevoir ma proposition par email' : 'Continuer';

    return (
        <SmartFormGlobal
            {...methods}
            className="mx-auto"
            onSubmit={handleSubmit}
            dontPassNextStep
        >
            <Question>
                <Question.Header>
                    {showHeaderIcon && HeaderAddressIcon && <HeaderAddressIcon />}
                    <Question.Title>{r(coordonneesContent.titleAdresse)}</Question.Title>
                </Question.Header>

                <FieldAdresses
                    nameAdresse="DemandeTarif.ListePersonnes.0.Adresse1"
                    nameComplement="DemandeTarif.ListePersonnes.0.Adresse2"
                    nameCP="DemandeTarif.ListePersonnes.0.CP"
                    nameVille="DemandeTarif.ListePersonnes.0.Ville"
                    validationCP={validationCP}
                    allowManualEntry={allowManualAddress}
                />
            </Question>

            <Question
                hideButton
                disabledAutoNext
            >
                <Question.Header>
                    {showHeaderIcon && HeaderCoordonnesIcon && <HeaderCoordonnesIcon />}
                    <Question.Title>{r(coordonneesContent.title)}</Question.Title>
                </Question.Header>

                <FieldInput
                    label={r(content.emailLabel)}
                    name="DemandeTarif.ListePersonnes[0].Email"
                    type="email"
                    autoComplete="email"
                    placeholder={r(content.emailPlaceholder)}
                    validation={{
                        required: "L'adresse email est requise",
                        validate: {
                            notCourtierEmail: validateNotCourtierEmail,
                        },
                    }}
                    description={isDirect && showEmailDematerialisationNotice ? r(coordonneesContent.emailDematerialisationNotice) : undefined}
                />

                <CourtierEmailWarning />

                <FieldInput
                    label={r(content.confirmEmailLabel)}
                    name="DemandeTarif.ListePersonnes[0].ConfirmEmail"
                    type="email"
                    autoComplete="email"
                    placeholder={r(content.confirmEmailPlaceholder)}
                    validation={{
                        required: "La confirmation de l'email est requise",
                        validate: {
                            isSame: (value) => value === methods.watch('DemandeTarif.ListePersonnes.0.Email') || 'Les adresses e-mail ne correspondent pas',
                        },
                    }}
                />

                {showEmailConsent && !emailConsentDejaRecueilli && (
                    <>
                        <BulleAide>
                            <p><strong>{r(coordonneesContent.emailConsentIntro)}</strong></p>
                            <p className="mb-4">{r(coordonneesContent.emailConsentReceive)}</p>
                            <p><strong>{r(coordonneesContent.emailConsentWhyTitle)}</strong></p>
                            <ul className="mb-4 space-y-1">
                                <li className="flex items-start gap-2">
                                    <CheckIcon className="w-5 h-5 text-secondary flex-shrink-0 mt-0.5" />
                                    <span>{r(coordonneesContent.emailConsentBullet1)}</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <CheckIcon className="w-5 h-5 text-secondary flex-shrink-0 mt-0.5" />
                                    <span>{r(coordonneesContent.emailConsentBullet2)}</span>
                                </li>
                                <li className="flex items-start gap-2">
                                    <CheckIcon className="w-5 h-5 text-secondary flex-shrink-0 mt-0.5" />
                                    <span>{r(coordonneesContent.emailConsentBullet3)}</span>
                                </li>
                            </ul>
                        </BulleAide>
                        <div className="w-full">
                            <FieldRadioBlock
                                name="DemandeDevis.EmailContactConsent"
                                options={[
                                    { value: true, label: "J'accepte" },
                                    { value: false, label: 'Je refuse' },
                                ]}
                                validation={{
                                    validate: (value) =>
                                        value === true || value === false || 'Veuillez faire un choix',
                                }}
                            />
                        </div>
                    </>
                )}

                <FieldPatternInput
                    type="phone"
                    label={r(content.telephoneLabel)}
                    name="DemandeTarif.ListePersonnes[0].TelPortable"
                    validation={{
                        required: 'Le numéro de téléphone est requis',
                    }}
                />

                {/* Consentement type checkbox (MAE) */}
                {showConsentCheckbox && (
                    <>
                        <HorairesRecontact />
                        <FieldCheckbox
                            name="DemandeDevis.OptinRecontact"
                            label={r(content.optinLabel)}
                            validation={{
                                required: 'Cette acceptation est requise',
                            }}
                        />
                        {isAprilMoto && <BulleAide>{r(content.optinDescription)}</BulleAide>}
                    </>
                )}

                {/* Consentement type radio (Vélo, Trottinette) */}
                {showConsentRadio && (
                    <>
                        <HorairesRecontact />
                        <BulleAide title={r(coordonneesContent.phoneConsentTitle)}>
                            <p>{r(coordonneesContent.phoneConsentSubtitle)}</p>
                            <ul className="list-disc list-inside mb-4">
                                <li>{r(coordonneesContent.phoneConsentBullet1)}</li>
                                <li>{r(coordonneesContent.phoneConsentBullet2)}</li>
                                <li>{r(coordonneesContent.phoneConsentBullet3)}</li>
                            </ul>
                        </BulleAide>
                        <div className="w-full">
                            <FieldRadioBlock
                                name="DemandeDevis.PhoneContactConsent"
                                options={[
                                    { value: true, label: "Oui, j'accepte d'être appelé" },
                                    { value: false, label: 'Non, continuer sans appel' },
                                ]}
                                validation={{
                                    required: 'Veuillez faire un choix',
                                }}
                            />
                        </div>
                        <ConsentRecontactLegal className="text-[13px]" />
                    </>
                )}

                {children}

                <SmartButton
                    loading={mutationSaveDevis.isPending}
                    value={buttonText}
                />
            </Question>

            <ModalOptionRecontact />
        </SmartFormGlobal>
    );
}

export const CoordonneesConsentStep = (props: Omit<CoordonneesStepProps, 'consentType'>) => (
    <CoordonneesStep {...props} consentType="radio" />
);
